/**
 * Application lock dialog – shown when applicant_lock === '1' after load (once).
 * Per README §3.2:
 * - Default: "Application is Under Review" / processing queue message.
 * - If any co-applicant has applicant_status === 'AWAITING': "Awaiting Co-Applicant Information" / co-applicant message.
 */

import { ValidationModal } from '@/components/ui/ValidationModal'
import { applicantValidationModalProps } from '@/features/applicant/constants/applicantDialogProps'
import type { ApplicantLockDialogProps } from '@/features/applicant/types/components'

export function ApplicantLockDialog({ open, onClose, applicationData }: ApplicantLockDialogProps) {
  const coResidents =
    applicationData?.co_resident_application ??
    (Array.isArray(applicationData?.co_resident) ? applicationData.co_resident : [])
  const anyCoResidentAwaiting =
    Array.isArray(coResidents) &&
    coResidents.some((r) => String(r?.applicant_status || '').toUpperCase() === 'AWAITING')

  const title = anyCoResidentAwaiting
    ? 'Awaiting Co-Applicant Information'
    : 'Application is Under Review'

  const message = anyCoResidentAwaiting
    ? 'Your application is now complete and locked as we await information from your co-applicant. You will be notified when we are ready for next steps'
    : 'Your application is in our processing queue. Nothing else is needed from you at this time. We will notify you when we are ready for the next steps.'

  return (
    <ValidationModal
      {...applicantValidationModalProps}
      isOpen={open}
      title={title}
      message={message}
      showValidationIcon={false}
      onClose={onClose}
      overlayDataTestId="applicant-modal-lock"
      closeButtonDataTestId="applicant-modal-lock-close-btn"
      confirmButtonDataTestId="applicant-modal-lock-ok-btn"
    />
  )
}
